import { ControlPointOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useMemo, useState } from "react";

import { inject } from "../../../../../../../lib/itn-ui-kit/services/service-container";
import useMedicalCompleteLock from "../../../../../../hooks/useMedicalCompleteLock";
import useWindowDimensions from "../../../../../../hooks/useWindowDimensions";
import MedicalCompleteLockNotice from "../../../../../global/MedicalCompleteLockNotice";
import Modal from "../../../../../global/Modal";
import { MEDICALLY_COMPLETE_LOCK_MESSAGE } from "../../../../../modules/ClientDetail/Case/utils/constants";

import ApptBillsActions from "./ApptBills/ApptBillsActions";
import ApptBillsList from "./ApptBills/ApptBillsList";
import { calculateTotalBill } from "./ApptBills/helpers";
import CreateApptProcedurev2 from "./CreateApptProcedurev2";
import DeleteApptProcedurev2 from "./DeleteApptProcedurev2";

export default function ProviderApptProceduresv2({ caseInfo, selectedAppointment }) {
  const currentUserService = inject("current-user-service");
  const { width } = useWindowDimensions();
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const billsLocked = isAppointmentLocked(caseInfo);
  const [openAddBill, setOpenAddBill] = useState(false);
  const [openDeleteBills, setOpenDeleteBills] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);

  const administeredServices =
    selectedAppointment?.administered_services || selectedAppointment?.administeredServices;

  const totalBill = calculateTotalBill(administeredServices);
  const canView = currentUserService?.userRoles?.permissions.includes(
    "provider.view_administeredservices"
  );
  const canAdd =
    currentUserService?.userRoles?.permissions.includes("provider.add_administeredservices") &&
    caseInfo;
  const canCreate = useMemo(() => selectedAppointment && canAdd, [selectedAppointment, canAdd]);

  function handleOpenAddModal(editionMode) {
    setIsEditing(editionMode);
    setTimeout(() => {
      setOpenAddBill(true);
    }, 500);
  }

  if (!canView) {
    return null;
  }

  return (
    <div className="relative">
      {/* Notes for small screens */}
      {width < 640 && selectedAppointment?.notes && (
        <div className="border-gray mb-4 w-full rounded-md border">
          <h3 className="px-6 py-2 underline">Appointment Notes</h3>
          <div
            className="notes p-4"
            dangerouslySetInnerHTML={{ __html: selectedAppointment.notes }}
          />
        </div>
      )}

      <MedicalCompleteLockNotice locked={billsLocked} className="mb-4" />

      <div className="mb-4 grid grid-cols-[1fr_3fr_1fr] gap-4">
        {/* Empty space */}
        <div></div>

        {/* Bill Total */}
        <span className="flex w-full flex-wrap place-content-center justify-self-end rounded-md bg-red-300 text-center">
          <b className="mr-2">Total Service Cost:</b>
          {totalBill}
        </span>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <ApptBillsActions
            selectedBills={selectedBills}
            handleOpenAddModal={() => handleOpenAddModal(true)}
            handleOpenDeleteModal={() => setOpenDeleteBills(true)}
            locked={billsLocked}
          />

          {canCreate && (
            <Tooltip
              className="w-fit"
              title={billsLocked ? MEDICALLY_COMPLETE_LOCK_MESSAGE : "Add Administered Service"}
              placement="top"
            >
              <a
                onClick={billsLocked ? undefined : () => handleOpenAddModal(false)}
                aria-disabled={billsLocked}
                className={
                  billsLocked
                    ? "flex cursor-not-allowed items-center rounded-lg px-2 text-gray-400"
                    : "flex cursor-pointer items-center rounded-lg px-2 text-green-500 hover:bg-green-500 hover:text-white"
                }
              >
                <ControlPointOutlined />
              </a>
            </Tooltip>
          )}
        </div>
      </div>

      {/* List */}
      <ApptBillsList
        selectedBills={selectedBills}
        setSelectedBills={setSelectedBills}
        bills={administeredServices || []}
        locked={billsLocked}
      />

      {/* Modals */}
      {openAddBill && (
        <Modal
          title={`${isEditing ? "Update" : "Create"} Administered Services`}
          open={openAddBill}
          maxWidth="md"
          onClose={() => setOpenAddBill(false)}
        >
          <CreateApptProcedurev2
            selectedAppt={selectedAppointment}
            setOpenAddBill={setOpenAddBill}
            caseInfo={caseInfo}
            selectedBills={isEditing ? selectedBills : []}
            setSelectedBills={setSelectedBills}
          />
        </Modal>
      )}
      {openDeleteBills && (
        <Modal
          maxWidth="xl"
          onClose={() => setOpenDeleteBills(false)}
          open
          title={
            <h2 className="text-center text-2xl capitalize text-red-500">
              Delete Administered Service<span className="lowercase">(s)</span>
            </h2>
          }
        >
          <DeleteApptProcedurev2
            selectedAppt={selectedAppointment}
            caseInfo={caseInfo}
            selectedBills={selectedBills}
            setSelectedBills={setSelectedBills}
            setOpenDeleteBills={setOpenDeleteBills}
          />
        </Modal>
      )}
    </div>
  );
}
